/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { registry } from '@/lib/page-designer/registry';

// STATIC_REGISTRY_START

const staticRegistryImporters = [
    () => import('../../components/announcement-banner/index'),
    () => import('../../components/content-card/index'),
    () => import('../../components/hero/index'),
    () => import('../../components/dynamic-image/index'),
    () => import('../../components/home/popular-category/index'),
    () => import('../../components/product-recommendations/index'),
    () => import('../../components/product-tile/index'),
    () => import('../../components/grid/index'),
    () => import('../../components/header/index'),
    () => import('../../components/hero-carousel/index'),
    () => import('../../components/navigation-menu-mega/index'),
    () => import('../../components/home/popular-categories/index'),
    () => import('../../components/product-carousel/index'),
    () => import('../../components/product-merchandising-grid/index'),
    () => import('../../components/affiliate-form/index'),
    () => import('../../components/announcement-message/index'),
    () => import('../../components/banner/index'),
    () => import('../../components/catalog-card/index'),
    () => import('../../components/cta-card/index'),
    () => import('../../components/image-card/index'),
    () => import('../../components/header-navigation/nav-column-link/index'),
    () => import('../../components/header-navigation/nav-link/index'),
    () => import('../../components/header-navigation/nav-service/index'),
    () => import('../../components/split-hero/index'),
    () => import('../../components/terrific-embed/index'),
    () => import('../../components/announcement-carousel/index'),
    () => import('../../components/carousel/index'),
    () => import('../../components/image-card-grid/index'),
    () => import('../../components/header-navigation/main-navigation/index'),
    () => import('../../components/header-navigation/nav-column/index'),
    () => import('../../components/header-navigation/nav-columns-menu/index'),
    () => import('../../components/header-navigation/nav-services-menu/index'),
    () => import('../../components/split-layout/index'),
] as const;

/**
 * Initialize the static component registry.
 * This function is auto-generated by the staticRegistry Vite plugin.
 *
 * DO NOT EDIT THIS FUNCTION MANUALLY - it will be overwritten on next build.
 *
 * Components registered: Content.announcementBanner, Content.contentCard, Content.hero, Content.pdImage, Content.popularCategory, Content.productRecommendations, Content.productTile, Layout.grid, Layout.header, Layout.heroCarousel, Layout.megaMenu, Layout.popularCategories, Layout.productCarousel, Layout.productMerchandisingGrid, priceshoes_content.affiliateForm, priceshoes_content.announcementMessage, priceshoes_content.banner, priceshoes_content.catalogCard, priceshoes_content.ctaCard, priceshoes_content.imageCard, priceshoes_content.navColumnLink, priceshoes_content.navLink, priceshoes_content.navService, priceshoes_content.splitHero, priceshoes_content.terrificEmbed, priceshoes_layout.announcementCarousel, priceshoes_layout.carousel, priceshoes_layout.imageCardGrid, priceshoes_layout.mainNavigation, priceshoes_layout.navColumn, priceshoes_layout.navColumnsMenu, priceshoes_layout.navServicesMenu, priceshoes_layout.splitLayout
 */
export function initializeRegistry(targetRegistry = registry): void {
    targetRegistry.registerImporter('Content.announcementBanner', staticRegistryImporters[0], { fallback: 'fallback' });
    targetRegistry.registerImporter('Content.contentCard', staticRegistryImporters[1]);
    targetRegistry.registerImporter('Content.hero', staticRegistryImporters[2]);
    targetRegistry.registerImporter('Content.pdImage', staticRegistryImporters[3]);
    targetRegistry.registerImporter('Content.popularCategory', staticRegistryImporters[4], { loader: 'loader' });
    targetRegistry.registerImporter('Content.productRecommendations', staticRegistryImporters[5], { loader: 'loader' });
    targetRegistry.registerImporter('Content.productTile', staticRegistryImporters[6], { loader: 'loader' });
    targetRegistry.registerImporter('Layout.grid', staticRegistryImporters[7]);
    targetRegistry.registerImporter('Layout.header', staticRegistryImporters[8]);
    targetRegistry.registerImporter('Layout.heroCarousel', staticRegistryImporters[9], { fallback: 'fallback' });
    targetRegistry.registerImporter('Layout.megaMenu', staticRegistryImporters[10]);
    targetRegistry.registerImporter('Layout.popularCategories', staticRegistryImporters[11], { loader: 'loader' });
    targetRegistry.registerImporter('Layout.productCarousel', staticRegistryImporters[12], {
        loader: 'loader',
        fallback: 'fallback',
    });
    targetRegistry.registerImporter('Layout.productMerchandisingGrid', staticRegistryImporters[13], {
        loader: 'loader',
        fallback: 'fallback',
    });
    targetRegistry.registerImporter('priceshoes_content.affiliateForm', staticRegistryImporters[14]);
    targetRegistry.registerImporter('priceshoes_content.announcementMessage', staticRegistryImporters[15]);
    targetRegistry.registerImporter('priceshoes_content.banner', staticRegistryImporters[16]);
    targetRegistry.registerImporter('priceshoes_content.catalogCard', staticRegistryImporters[17]);
    targetRegistry.registerImporter('priceshoes_content.ctaCard', staticRegistryImporters[18]);
    targetRegistry.registerImporter('priceshoes_content.imageCard', staticRegistryImporters[19]);
    targetRegistry.registerImporter('priceshoes_content.navColumnLink', staticRegistryImporters[20]);
    targetRegistry.registerImporter('priceshoes_content.navLink', staticRegistryImporters[21]);
    targetRegistry.registerImporter('priceshoes_content.navService', staticRegistryImporters[22]);
    targetRegistry.registerImporter('priceshoes_content.splitHero', staticRegistryImporters[23]);
    targetRegistry.registerImporter('priceshoes_content.terrificEmbed', staticRegistryImporters[24], {
        loader: 'loader',
    });
    targetRegistry.registerImporter('priceshoes_layout.announcementCarousel', staticRegistryImporters[25]);
    targetRegistry.registerImporter('priceshoes_layout.carousel', staticRegistryImporters[26]);
    targetRegistry.registerImporter('priceshoes_layout.imageCardGrid', staticRegistryImporters[27]);
    targetRegistry.registerImporter('priceshoes_layout.mainNavigation', staticRegistryImporters[28]);
    targetRegistry.registerImporter('priceshoes_layout.navColumn', staticRegistryImporters[29]);
    targetRegistry.registerImporter('priceshoes_layout.navColumnsMenu', staticRegistryImporters[30]);
    targetRegistry.registerImporter('priceshoes_layout.navServicesMenu', staticRegistryImporters[31]);
    targetRegistry.registerImporter('priceshoes_layout.splitLayout', staticRegistryImporters[32]);
}

// STATIC_REGISTRY_END
